import React, { useState } from 'react';
import backendApi from '../../services/backendApi';

/**
 * Composant de debug pour tester l'API
 */
const ApiDebug = () => {
  const [logs, setLogs] = useState([]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
    console.log(`[${timestamp}] ${message}`);
  };

  const testHealth = async () => {
    try {
      addLog('🧪 Test health check...', 'info');
      const response = await fetch('http://localhost:3001/health', {
        credentials: 'include'
      });
      const data = await response.json();
      addLog(`✅ Health OK: ${JSON.stringify(data)}`, 'success');
    } catch (error) {
      addLog(`❌ Health Error: ${error.message}`, 'error');
    }
  };

  const testBackendApi = async () => {
    try {
      addLog('🧪 Test via backendApi.healthCheck()...', 'info');
      const data = await backendApi.healthCheck();
      addLog(`✅ Backend API OK: ${JSON.stringify(data)}`, 'success');
    } catch (error) {
      addLog(`❌ Backend API Error: ${error.message}`, 'error');
    }
  };

  const testCORS = async () => {
    try {
      addLog('🧪 Test CORS...', 'info');
      const response = await fetch('http://localhost:3001/health', {
        method: 'OPTIONS',
        headers: {
          'Access-Control-Request-Method': 'GET',
          'Origin': window.location.origin
        }
      });
      addLog(`✅ CORS Status: ${response.status}`, 'success');
      addLog(`CORS Headers: ${JSON.stringify([...response.headers.entries()])}`, 'info');
    } catch (error) {
      addLog(`❌ CORS Error: ${error.message}`, 'error');
    }
  };

  const testWithCredentials = async () => {
    try {
      addLog('🧪 Test avec credentials...', 'info');
      const response = await fetch('http://localhost:3001/api/projects', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      addLog(`Status: ${response.status} ${response.statusText}`, 'info');
      const data = await response.json();
      addLog(`Response: ${JSON.stringify(data)}`, response.ok ? 'success' : 'error');
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">🔧 API Debug Tool</h1>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Tests</h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={testHealth}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Test Health Check
            </button>
            <button
              onClick={testBackendApi}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Test Backend API Service
            </button>
            <button
              onClick={testCORS}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
            >
              Test CORS
            </button>
            <button
              onClick={testWithCredentials}
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
            >
              Test Projects (No Auth)
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Logs</h2>
            <button
              onClick={clearLogs}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
            >
              Clear
            </button>
          </div>

          <div className="bg-gray-900 text-gray-100 rounded p-4 font-mono text-sm max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-500">Aucun log. Cliquez sur un bouton pour tester.</p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className="mb-2">
                  <span className="text-gray-500">[{log.timestamp}]</span>{' '}
                  <span className={
                    log.type === 'error' ? 'text-red-400' :
                    log.type === 'success' ? 'text-green-400' :
                    'text-blue-400'
                  }>
                    {log.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Info</h3>
          <ul className="text-sm text-brand-teal-800 space-y-1">
            <li>• Backend URL: {process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}</li>
            <li>• Frontend Origin: {window.location.origin}</li>
            <li>• Cookies enabled: {navigator.cookieEnabled ? 'Oui' : 'Non'}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ApiDebug;
